import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Drawer,
  Table,
  DatePicker,
  Card,
  message,
} from "antd";
import { useLocation } from "react-router-dom";
import LeaveService from "../../Services/Manage/LeaveService";
import CheckInService from "../../Services/Manage/CheckInService";
import dayjs from "dayjs";

const { Text } = Typography;

function SalaryIssues() {
  const location = useLocation();
  const { user_id, month, leaves = [] } = location.state || {};

  const [salaryData, setSalaryData] = useState(null);
  const [attendanceSummary, setAttendanceSummary] = useState({
    totalWorkingHours: "0h 0m",
  });
  const [calculatedSalary, setCalculatedSalary] = useState(0);
  const [isApproved, setIsApproved] = useState(false);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [approvedSalaries, setApprovedSalaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const empUid = user_id;
  const displayMonth = month ? dayjs(month + "-01").format("MMM YYYY") : "";

  const fetchSalary = async () => {
    try {
      const data = await LeaveService.getEmployeeSalary(empUid);
      const salary = Array.isArray(data) ? data[0] : data;
      setSalaryData(salary);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMonthlyAttendance = async () => {
    try {
      const res = await CheckInService.getCheckInHistoryByMonth(empUid, month);
      if (res.status && res.data.length) {
        const attendanceData = res.data;
        const totalSeconds = attendanceData.reduce((acc, entry) => {
          const checkIn = new Date(entry.check_in_time).getTime();
          const checkOut = entry.check_out_time
            ? new Date(entry.check_out_time).getTime()
            : Date.now();
          const workSeconds = (checkOut - checkIn) / 1000;
          const breakSeconds =
            entry.breaks?.reduce((bAcc, br) => bAcc + (br.duration || 0), 0) ||
            0;
          return acc + Math.max(workSeconds - breakSeconds, 0);
        }, 0);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        setAttendanceSummary({ totalWorkingHours: `${hours}h ${minutes}m` });
      } else {
        setAttendanceSummary({ totalWorkingHours: "0h 0m" });
      }
    } catch (err) {
      console.error(err);
    }
  };

  function parseHoursToDecimal(hoursStr) {
    const [h, m] = hoursStr.split("h").map((v) => v.trim().replace("m", ""));
    const hours = parseInt(h, 10) || 0;
    const minutes = parseInt(m, 10) || 0;
    return hours + minutes / 60;
  }

  const totalLeaveHours = leaves.reduce((sum, leave) => {
    const from = dayjs(leave.from_date);
    const to = dayjs(leave.to_date);
    const days = to.diff(from, "day") + 1;
    const hoursPerDay = 8;
    return sum + days * hoursPerDay;
  }, 0);

  const totalWorkedHoursDecimal = parseHoursToDecimal(
    attendanceSummary.totalWorkingHours
  );
  const totalWithPaidLeave = totalWorkedHoursDecimal + totalLeaveHours;

  useEffect(() => {
    if (salaryData) {
      const workedHours = totalWorkedHoursDecimal;
      const expectedHours = parseFloat(salaryData.working_hours || 0);
      const salaryAmount = parseFloat(salaryData.salary_amount || 0);
      const calculated =
        expectedHours > 0 ? (workedHours / expectedHours) * salaryAmount : 0;
      setCalculatedSalary(isNaN(calculated) ? 0 : calculated.toFixed(2));
    }
  }, [salaryData, attendanceSummary]);

  useEffect(() => {
    if (empUid && month) {
      fetchSalary();
      fetchMonthlyAttendance();
    }
  }, [empUid, month]);

  const handleApproveSalary = async () => {
    if (!salaryData) {
      message.error("Salary data not loaded yet!");
      return;
    }
    if (attendanceSummary.totalWorkingHours === "0h 0m") {
      message.warning(
        "Cannot approve salary: Total worked hours is 0 for this month."
      );
      return;
    }

    const omeUser = JSON.parse(localStorage.getItem("ome_user"));
    const approvedBy = omeUser?.added_by || "Unknown";

    const totalWorked =
      `${Math.floor(totalWithPaidLeave)}h ${Math.floor(
        (totalWithPaidLeave % 1) * 60
      )}m` || "0h 0m";

    const calculatedSalaryValue = (
      (totalWithPaidLeave /
        parseFloat(salaryData?.working_hours || 1)) *
      (salaryData?.salary_amount || 0)
    ).toFixed(2);

    const payload = {
      emp_id: empUid,
      approved_by: approvedBy,
      total_worked_hour: totalWorked,
      expected_hour: salaryData.working_hours,
      salary_amount: salaryData.salary_amount,
      calculated_monthly_salary: isNaN(calculatedSalaryValue)
        ? "0.00"
        : calculatedSalaryValue,
      month,
    };

    try {
      const res = await CheckInService.approveSalary(payload);
      if (res.status) {
        message.success("Salary approved successfully!");
        setIsApproved(true);
      } else {
        message.error(res.message || "Failed to approve salary.");
      }
    } catch (err) {
      console.error(err);
      message.error("Salary already approved for this month");
    }
  };

  const salaryRowData = [
    {
      key: "1",
      label: "Without Paid Leave",
      totalWorked: attendanceSummary.totalWorkingHours,
      expectedHours: `${salaryData?.working_hours || 0}h`,
      salaryAmount: `₹${salaryData?.salary_amount || 0}`,
   calculatedSalary: `₹${isNaN(calculatedSalary) ? 0 : parseFloat(calculatedSalary) || 0}`,

    },
    {
      key: "2",
      label: "Including Paid Leave",
      totalWorked: `${Math.floor(totalWithPaidLeave)}h ${Math.floor(
        (totalWithPaidLeave % 1) * 60
      )}m`,
      expectedHours: `${salaryData?.working_hours || 0}h`,
      salaryAmount: `₹${salaryData?.salary_amount || 0}`,
  calculatedSalary: `₹${
  isNaN(
    (totalWithPaidLeave / parseFloat(salaryData?.working_hours || 1)) *
      (salaryData?.salary_amount || 0)
  )
    ? 0
    : parseFloat(
        (
          (totalWithPaidLeave / parseFloat(salaryData?.working_hours || 1)) *
          (salaryData?.salary_amount || 0)
        ).toFixed(0)
      )
}`,

    },
  ];

  const salaryRowColumns = [
    {
      title: "Type",
      dataIndex: "label",
      key: "label",
      align: "center",
    },
    {
      title: "Total Worked Hours (month)",
      dataIndex: "totalWorked",
      key: "totalWorked",
      align: "center",
    },
    {
      title: "Expected Hours (month)",
      dataIndex: "expectedHours",
      key: "expectedHours",
      align: "center",
    },
    {
      title: "Salary Amount (month)",
      dataIndex: "salaryAmount",
      key: "salaryAmount",
      align: "center",
    },
    {
      title: "Calculated Salary for month",
      dataIndex: "calculatedSalary",
      key: "calculatedSalary",
      align: "center",
    },
  ];

  const handleMonthChange = (date) => {
    const monthStr = date ? date.format("YYYY-MM") : null;
    setSelectedMonth(monthStr);
    fetchApprovedSalaries(monthStr);
  };

  const fetchApprovedSalaries = async (monthStr) => {
    setLoading(true);
    try {
      const res = await CheckInService.getAllSalaries();
      if (res.status) {
        let data = res.data;
        if (monthStr) data = data.filter((item) => item.month.startsWith(monthStr));
        setApprovedSalaries(data);
      } else {
        message.warning(res.message || "No approved salaries found");
        setApprovedSalaries([]);
      }
    } catch (err) {
      console.error(err);
      setApprovedSalaries([]);
    }
    setLoading(false);
  };

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text strong style={{ fontSize: 18 }}>
            Salary Calculation - {displayMonth}
          </Text>
          <Button
            type={isApproved ? "default" : "primary"}
            onClick={handleApproveSalary}
            disabled={isApproved || attendanceSummary.totalWorkingHours === "0h 0m"}
          >
            {isApproved ? "Approved" : "Approve"}
          </Button>
        </div>
      }
    >
      <Table
        dataSource={salaryRowData}
        columns={salaryRowColumns}
        pagination={false}
        
        size="middle"
      />

      <div style={{ marginTop: 24, textAlign: "center" }}>
        <Button type="default" onClick={() => setDrawerVisible(true)}>
          View Approved Salaries
        </Button>
      </div>

      <Drawer
        title="Approved Salaries"
        width={500}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>Select Month: </Text>
          <DatePicker.MonthPicker
            onChange={handleMonthChange}
            placeholder="Select month"
            style={{ marginLeft: 8 }}
          />
        </div>

        <Table
          dataSource={approvedSalaries}
          columns={[
            { title: "Employee ID", dataIndex: "emp_id", key: "emp_id" },
            { title: "Worked Hours", dataIndex: "total_worked_hour", key: "total_worked_hour" },
            {
              title: "Calculated Salary",
              dataIndex: "calculated_monthly_salary",
              key: "calculated_monthly_salary",
              render: (val) => `₹${val || 0}`,
            },
            {
              title: "Month",
              dataIndex: "month",
              key: "month",
              render: (val) => dayjs(val + "-01").format("MMM YYYY"),
            },
          ]}
          rowKey={(record) => record.emp_id + record.month}
          loading={loading}
          locale={{ emptyText: "No approved salaries available" }}
        />
      </Drawer>
    </Card>
  );
}

export default SalaryIssues;
